import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "./NcdForm.css";
import { ncdAPI } from "../../../services/ncd.api";
import { useNCD } from "../hooks/useNCD";
import { formatToInputDate, formatToApiDate } from "../../../helpers/common-helper";

export const CervicalScreeningCHO = ({ individualId, cervicalScreeningId, viewMode, onIdUpdate, selectedUser,setUserError }) => {
    const { screeningData, setLoading, setScreeningData, setError,clearScreeningData } = useNCD();
    const today = new Date().toISOString().split("T")[0];
    const cervicalData = screeningData[`cervical_${individualId}`];

    const [selectedCervicalDots1, setSelectedCervicalDots1] = useState(Array(12).fill(false));

    const [formData, setFormData] = useState({
        anyIssuesInCervix: false,
        assessmentDate: today,
        excessiveBleedingInPeriods: false,
        bleedingBetweenPeriods: false,
        intercourseBleeding: false,
        foulSmellingDischarge: false,
        postMenopauseBleeding: false,
        symptomRemarks: "",
        refusedScreening: false,
        polyp: false,
        ectopy: false,
        hypertrophy: false,
        bleedsOnTouch: false,
        suspiciousLooking: false,
        unhealthyCervix: false,
        frankMalignancy: false,
        prolapseUterus: false,
        excessiveDischarge: false,
        examRemarks: "",
        viaTrained: false,
        viaTestResult: "",
        viaPositivePoints: [],
        referralReferred: false,
        referralTo: "",
        referralToOther: "",
        referralRemarks: "",
    });

    useEffect(() => {
        const fetchData = async (cervicalScreeningId) => {
            try {
                setLoading(true);
                const responseData = await ncdAPI.getCervicalScreeningCHO(individualId, cervicalScreeningId);
                setScreeningData(`cervical_${individualId}`, responseData?.data);

                const newSelected = Array(12).fill(false);

                responseData?.data?.via.viaPoints.forEach(item => {
                    const match = item.match(/CPS(\d+)/);
                    if (match) {
                        const dotIndex = parseInt(match[1], 10) - 1;
                        if (dotIndex >= 0 && dotIndex < 12) {
                            newSelected[dotIndex] = true;
                        }
                    }
                });

                setSelectedCervicalDots1(newSelected);

                setLoading(false);
            } catch (err) {
                setError("Failed to fetch cervical screening data");
                setLoading(false);
            }
        };

        if (viewMode === 'VIEW' && cervicalScreeningId) {
            fetchData(cervicalScreeningId);
        }
    }, [viewMode, cervicalScreeningId, individualId]);

    // Simulate getting data from API

    const mapViaValue = (value) => {
        switch (value) {
            case 1:
            case '1':
                return 'Positive';
            case 2:
            case '2':
                return 'Negative';
            case 3:
            case '3':
                return 'Suspicious';
            default:
                return '';
        }
    };

    const handleFieldChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

         if (!selectedUser) {
            setUserError(true);
        }

        const username = selectedUser || "";

        const payload = {
            symptoms: {
                anyIssuesInCervix: hasCervicalSymptoms === "yes",
                date: formatToApiDate(formData.assessmentDate),
                excessiveBleedingInPeriods: formData.excessiveBleedingInPeriods === "yes",
                bleedingBetweenPeriods: formData.bleedingBetweenPeriods === "yes",
                intercourseBleeding: formData.intercourseBleeding === "yes",
                foulSmellingDischarge: formData.foulSmellingDischarge === "yes",
                postMenopauseBleeding: formData.postMenopauseBleeding === "yes",
                remarks: formData.symptomRemarks
            },
            examination: {
                refusedScreening: isRefused,
                polyp: formData.polyp === "yes",
                ectopy: formData.ectopy === "yes",
                hypertrophy: formData.hypertrophy === "yes",
                bleedsOnTouch: formData.bleedsOnTouch === "yes",
                suspiciousLooking: formData.suspiciousLooking === "yes",
                unhealthyCervix: formData.unhealthyCervix === "yes",
                frankMalignancy: formData.frankMalignancy === "yes",
                prolapseUterus: formData.prolapseUterus === "yes",
                excessiveDischarge: formData.excessiveDischarge === "yes",
                remarks: formData.examRemarks
            },
            via: {
                via: dropdownValue === "Positive" ? 1 : 0,
                viaPoints: Object.entries(selectedCervicalDots)
                    .filter(([_, v]) => v)
                    .map(([i]) => `CPS${parseInt(i) + 1}`)
            },
            referral: {
                referred: referCervical === "Yes",
                referredTo: referCervicalTo || null,
                referredToIfOther: referCervicalOther || null,
                remarks: formData.referralRemarks
            },
            createdUser: username,
            screeningANM: {
                users: username,
                anmValue: username,
                viaTrained: true
            },
            currentScreeningId: null,
            screeningType: "FirstTime Screening"
        };

        try {
            setLoading(true);
            const response = await ncdAPI.submitCervicalScreeningCHO(individualId, payload);

            if (response.success) {
                if (response?.data?.screeningId) {
                    onIdUpdate(response?.data?.screeningId);
                }
                toast.success(`Cervical cancer screening data has saved successfully`);

                clearScreeningData(`cervical_${individualId}`); // Clear cache to ensure fresh data on next view
                
                // Reset form fields
                setFormData({
                    anyIssuesInCervix: false,
                    assessmentDate: today,
                    excessiveBleedingInPeriods: false,
                    bleedingBetweenPeriods: false,
                    intercourseBleeding: false,
                    foulSmellingDischarge: false,
                    postMenopauseBleeding: false,
                    symptomRemarks: "",
                    refusedScreening: false,
                    polyp: false,
                    ectopy: false,
                    hypertrophy: false,
                    bleedsOnTouch: false,
                    suspiciousLooking: false,
                    unhealthyCervix: false,
                    frankMalignancy: false,
                    prolapseUterus: false,
                    excessiveDischarge: false,
                    examRemarks: "",
                    viaTrained: false,
                    viaTestResult: "",
                    viaPositivePoints: [],
                    referralReferred: false,
                    referralTo: "",
                    referralToOther: "",
                    referralRemarks: "",
                });
                setHasCervicalSymptoms("no");
                setIsRefused(false);
                setreferCervical("");
                setReferCervicalTo("");
                setReferCervicalOther("");
                setDropdownValue("");
                setSelectedCervicalDots({});
            } else {
                toast.error(response.message || "Failed to save data", { className: "custom-toast-error" });
            }
            setLoading(false);
        } catch (error) {
            toast.error(error.response?.data?.message || "Something went wrong", { className: "custom-toast-error" });
            setLoading(false);
        }

    };

    // =============================Jsx for servical issues ===============================
    const [hasCervicalSymptoms, setHasCervicalSymptoms] = useState("no");
    const [isRefused, setIsRefused] = useState(false);
    const handleCervicalChange = (e) => {
        setHasCervicalSymptoms(e.target.value);
    };

    const isDisabled = hasCervicalSymptoms === "no";
    const handleCheckboxChangecervical = (e) => {
        setIsRefused(e.target.checked);
    };

    const [referCervical, setreferCervical] = useState("");
    const [referCervicalTo, setReferCervicalTo] = useState("");
    const [referCervicalOther, setReferCervicalOther] = useState("");

    // Handler for refer radio
    const handleReferCervicalChange = (e) => {
        const value = e.target.value;
        setreferCervical(value);
        if (value === "No") {
            setReferCervicalTo("");
            setReferCervicalOther("");
        }
    };

    const handleReferCervicalToChange = (value) => {
        setReferCervicalTo(value);
        if (value !== "Others") {
            setReferCervicalOther("");
        }
    };

    // =============================Jsx for servical issues ===============================

    const [dropdownValue, setDropdownValue] = useState("");
    const [selectedCervicalDots, setSelectedCervicalDots] = useState({});

    const handleDotCirvicialClick = (index) => {
        if (dropdownValue === "Negative") return;
        if (dropdownValue === "Positive" || dropdownValue === "Suspicious") {
            setSelectedCervicalDots((prev) => ({
                ...prev,
                [index]: !prev[index],
            }));
        }
    };

    return (
        <>

            {viewMode === "ADD" ? (
                <form onSubmit={handleSubmit}>
                    <div className="oral-cancer-table">

                        <div className="row">
                            <div className="col-xs-12 col-md-8"></div>
                            <div className="col-xs-12 col-md-4">
                                <input
                                    type="date"
                                    className="form-control"
                                    value={formData.assessmentDate}
                                    max={new Date().toISOString().split('T')[0]}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            assessmentDate: e.target.value
                                        }))
                                    }
                                    required
                                />
                            </div>
                        </div>

                        <div className="headerTitle">
                            <div className="titleSection"><h4>Symptoms</h4></div>
                        </div>
                        <div className="choCancerFeildsSection">
                            <div className="row">
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/any_cervical_problem.png"} alt="Any cervical problem" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>
                                                    Any cervical related symptoms?<span className="Refre-red">*</span></p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="anyIssuesInCervix" className="form-check-input" value="yes" checked={hasCervicalSymptoms === "yes"} onChange={handleCervicalChange}
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="anyIssuesInCervix"
                                                        className="form-check-input"
                                                        value="no"
                                                        checked={hasCervicalSymptoms === "no"}
                                                        onChange={handleCervicalChange}
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/excessive_bleeding_during_periods.png"} alt="Excessive bleeding during periods" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Excessive bleeding during periods?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="excessiveBleedingInPeriods" className="form-check-input" value="yes" onChange={handleFieldChange} disabled={isDisabled} />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="excessiveBleedingInPeriods" className="form-check-input" value="no" onChange={handleFieldChange} disabled={isDisabled} />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/bleeding_between_periods.png"} alt="Bleeding between periods" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Bleeding between periods?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="bleedingBetweenPeriods" className="form-check-input" value="yes" onChange={handleFieldChange} disabled={isDisabled} />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="bleedingBetweenPeriods" className="form-check-input" value="no" onChange={handleFieldChange} disabled={isDisabled} />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/bleeding_after_intercourse.png"} alt="Bleeding after intercourse" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Bleeding after intercourse?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="intercourseBleeding" className="form-check-input" value="yes" onChange={handleFieldChange} disabled={isDisabled} />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="intercourseBleeding" className="form-check-input" value="no" onChange={handleFieldChange} disabled={isDisabled} />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/foul_smell.png"} alt="Foul smell" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>
                                                    Excessive foul smelling vaginal discharge?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="foulSmellingDischarge" className="form-check-input" value="yes" onChange={handleFieldChange} disabled={isDisabled} />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="foulSmellingDischarge" className="form-check-input" value="no" onChange={handleFieldChange} disabled={isDisabled} />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/post_menopausal_bleeding.png"} alt="Post menopausal bleeding" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>
                                                    Postmenopausal bleeding?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="postMenopauseBleeding" className="form-check-input" value="yes" onChange={handleFieldChange} disabled={isDisabled} />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="postMenopauseBleeding" className="form-check-input" value="no" onChange={handleFieldChange} disabled={isDisabled} />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-xs-12 col-md-12">
                                    <div className="SymptomsFeild remarksRow">
                                        <div className="symptomsImg">
                                            <img src={"/images/Remarks.png"} alt="Remarks" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Remarks</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <textarea type="text" rows="3" value={formData.symptomRemarks || ""} onChange={(e) => setFormData({ ...formData, symptomRemarks: e.target.value })}></textarea>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="headerTitle ">
                            <div className="titleSection"><h4>Visual Examination</h4></div>
                        </div>
                        <div className="choCancerFeildsSection">
                            <div className="row">
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">

                                        <div className="symptomsDetaisl">
                                            <label className="checkboxLabel">
                                                <input type="checkbox" name="refusedScreening" checked={isRefused} onChange={handleCheckboxChangecervical} />
                                                <span>Patient has refused screening</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/polyp.png"} alt="Polyp" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>
                                                    Polyp?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="polyp" className="form-check-input" value="yes" onChange={handleFieldChange} disabled={isRefused} />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="polyp" className="form-check-input" value="no" onChange={handleFieldChange} disabled={isRefused} />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/ectopy.png"} alt="Ectopy" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>
                                                    Ectopy?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="ectopy" value="yes" className="form-check-input" onChange={handleFieldChange} disabled={isRefused} />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="ectopy" value="no" className="form-check-input" onChange={handleFieldChange} disabled={isRefused} />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/ectopy.png"} alt="Ectopy" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>
                                                    Hypertrophy?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="hypertrophy" value="yes" className="form-check-input" onChange={handleFieldChange} disabled={isRefused} />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="hypertrophy" value="no" className="form-check-input" onChange={handleFieldChange} disabled={isRefused} />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/bleeds_on_touch.png"} alt="Bleeds on touch" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>
                                                    Bleeds on touch?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="bleedsOnTouch" className="form-check-input" value="yes" onChange={handleFieldChange} disabled={isRefused} />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="bleedsOnTouch" className="form-check-input" value="no" onChange={handleFieldChange} disabled={isRefused} />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/polyp.png"} alt="Polyp" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Unhealthy cervix?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="unhealthyCervix" className="form-check-input" value="yes" onChange={handleFieldChange} disabled={isRefused} />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="unhealthyCervix" className="form-check-input" value="no" onChange={handleFieldChange} disabled={isRefused} />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/suspecious_looking.png"} alt="Suspicious looking" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Suspicious looking?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="suspiciousLooking" className="form-check-input" value="yes" onChange={handleFieldChange} disabled={isRefused} />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="suspiciousLooking" className="form-check-input" value="no" onChange={handleFieldChange} disabled={isRefused} />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/polyp.png"} alt="Polyp" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>
                                                    Frank malignancy/growth in cervix?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="frankMalignancy" className="form-check-input" value="yes" onChange={handleFieldChange} disabled={isRefused} />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="frankMalignancy" className="form-check-input" value="no" onChange={handleFieldChange} disabled={isRefused} />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/prolapse_uterus.png"} alt="Prolapse uterus" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Prolapse uterus?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="prolapseUterus" className="form-check-input" value="yes" onChange={handleFieldChange} disabled={isRefused} />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="prolapseUterus" className="form-check-input" value="no" onChange={handleFieldChange} disabled={isRefused} />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/polyp.png"} alt="Polyp" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>
                                                    Excessive discharge?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="excessiveDischarge" className="form-check-input" value="yes" onChange={handleFieldChange} disabled={isRefused} />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="excessiveDischarge" className="form-check-input" value="no" onChange={handleFieldChange} disabled={isRefused} />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-12">
                                    <div className="SymptomsFeild remarksRow">
                                        <div className="symptomsImg">
                                            <img src={"/images/Remarks.png"} alt="Remarks" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Remarks</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <textarea type="text" rows="3" value={formData.examRemarks || ""} onChange={(e) => setFormData({ ...formData, examRemarks: e.target.value })}></textarea>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="cirvicalImgSection cirvicalImgSectionCHO">

                            <div className="headerSectionBreastCHO">
                                <div className="feildsArea">
                                    <select
                                        value={dropdownValue}
                                        onChange={(e) => {
                                            setDropdownValue(e.target.value);
                                            if (e.target.value === "Negative") {
                                                setSelectedCervicalDots({});
                                            }
                                        }}
                                    >
                                        <option value="">Select VIA Points</option>
                                        <option value="Positive">Positive</option>
                                        <option value="Negative">Negative</option>
                                        <option value="Suspicious">Suspicious</option>
                                    </select>
                                </div>
                                <div className="breastheadings">
                                    <h5>Select findings from dropdown and mark the points below</h5>
                                </div>
                            </div>

                            <div className="circle-container">
                                <img src={"/images/cervical_cancer_diagram.png"} style={{ width: '325px', height: '329px' }} alt="Cervical cancer diagram" />
                                {Array.from({ length: 12 }).map((_, i) => {

                                    const rotatedIndex = (i + 1) % 12; // Rotate clockwise by 1
                                    const angle = (360 / 12) * rotatedIndex - 90;
                                    const radius = 120;
                                    const x = radius * Math.cos((angle * Math.PI) / 180);
                                    const y = radius * Math.sin((angle * Math.PI) / 180);

                                    return (
                                        <div
                                            key={i}
                                            className={`dotCirvical ${selectedCervicalDots[i] ? "active" : ""}`}
                                            style={{
                                                transform: `translate(${x}px, ${y}px)`,
                                            }}
                                            onClick={() => handleDotCirvicialClick(i)}
                                        >
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="headerTitle ">
                            <div className="titleSection"><h4>Referral</h4></div>
                        </div>
                        <div className="choCancerFeildsSection">
                            <div className="row">
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/Refer.png"} alt="Refer" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Refer</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="referred" value="Yes" className="form-check-input" checked={referCervical === "Yes"} onChange={handleReferCervicalChange} />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="referred" value="No" className="form-check-input" checked={referCervical === "No"} onChange={handleReferCervicalChange} />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-6">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/Refer.png"} alt="Refer" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Refer to {referCervical === "Yes" && <span className="text-danger">*</span>} </p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <select
                                                    value={referCervicalTo}
                                                    onChange={e => handleReferCervicalToChange(e.target.value)}
                                                    disabled={referCervical !== "Yes"}
                                                >
                                                    <option value="">Select</option>
                                                    {/* <option value="PHC">PHC</option> */}
                                                    <option value="CHC">CHC</option>
                                                    <option value="District hospital">District Hospital</option>
                                                    <option value="Others">Other</option>
                                                </select>
                                            </div>
                                            {referCervical === "Yes" && !referCervicalTo && (
                                                <div className="text-danger mt-1" style={{ fontSize: "9px", fontWeight: "700" }}>This field is required.</div>
                                            )}
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>If other, specify</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <input
                                                    type="text"
                                                    value={referCervicalOther}
                                                    onChange={e => setReferCervicalOther(e.target.value)}
                                                    disabled={referCervicalTo !== "Others"}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-12">
                                    <div className="SymptomsFeild remarksRow">
                                        <div className="symptomsImg">
                                            <img src={"/images/Remarks.png"} alt="Remarks" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Remarks</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <textarea type="text" rows="3" value={formData.referralRemarks || ""} onChange={(e) => setFormData({ ...formData, referralRemarks: e.target.value })}></textarea>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="save-button-container">
                            <button type="submit" className="save-button">Save & Continue</button>
                        </div>
                    </div>
                </form>
            ) : (
                <>
                    <div className="oral-cancer-table">

                        <div className="row">
                            <div className="col-xs-12 col-md-8"></div>
                            <div className="col-xs-12 col-md-4">
                                <input
                                    type="date"
                                    className="form-control"
                                    value={formatToInputDate(cervicalData?.symptoms?.date)}
                                    disabled
                                />
                            </div>
                        </div>

                        <div className="headerTitle">
                            <div className="titleSection"><h4>Symptoms</h4></div>
                        </div>
                        <div className="choCancerFeildsSection">
                            <div className="row">
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/any_cervical_problem.png"} alt="Any cervical problem" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>
                                                    Any cervical related symptoms?<span className="Refre-red">*</span></p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="anyIssuesInCervix" className="form-check-input" checked={cervicalData?.symptoms?.anyIssuesInCervix === true} disabled
                                                    />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="anyIssuesInCervix"
                                                        className="form-check-input"
                                                        checked={cervicalData?.symptoms?.anyIssuesInCervix === false}
                                                        disabled
                                                    />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/excessive_bleeding_during_periods.png"} alt="Excessive bleeding during periods" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Excessive bleeding during periods?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="excessiveBleedingInPeriods" className="form-check-input" checked={cervicalData?.symptoms?.excessiveBleedingInPeriods === true} disabled />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="excessiveBleedingInPeriods" className="form-check-input" checked={cervicalData?.symptoms?.excessiveBleedingInPeriods === false} disabled />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/bleeding_between_periods.png"} alt="Bleeding between periods" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Bleeding between periods?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="bleedingBetweenPeriods" className="form-check-input" checked={cervicalData?.symptoms?.bleedingBetweenPeriods === true} disabled />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="bleedingBetweenPeriods" className="form-check-input" checked={cervicalData?.symptoms?.bleedingBetweenPeriods === false} disabled />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/bleeding_after_intercourse.png"} alt="Bleeding after intercourse" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Bleeding after intercourse?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="intercourseBleeding" className="form-check-input" checked={cervicalData?.symptoms?.intercourseBleeding === true} disabled />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="intercourseBleeding" className="form-check-input" checked={cervicalData?.symptoms?.intercourseBleeding === false} disabled />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/foul_smell.png"} alt="Foul smell" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>
                                                    Excessive foul smelling vaginal discharge?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="foulSmellingDischarge" className="form-check-input" checked={cervicalData?.symptoms?.foulSmellingDischarge === true} disabled />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="foulSmellingDischarge" className="form-check-input" checked={cervicalData?.symptoms?.foulSmellingDischarge === false} disabled />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/post_menopausal_bleeding.png"} alt="Post menopausal bleeding" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>
                                                    Postmenopausal bleeding?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="postMenopauseBleeding" className="form-check-input" checked={cervicalData?.symptoms?.postMenopauseBleeding === true} disabled />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="postMenopauseBleeding" className="form-check-input" checked={cervicalData?.symptoms?.postMenopauseBleeding === false} disabled />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-xs-12 col-md-12">
                                    <div className="SymptomsFeild remarksRow">
                                        <div className="symptomsImg">
                                            <img src={"/images/Remarks.png"} alt="Remarks" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Remarks</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <textarea type="text" rows="3" value={cervicalData?.symptoms?.remarks} disabled></textarea>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="headerTitle ">
                            <div className="titleSection"><h4>Visual Examination</h4></div>
                        </div>
                        <div className="choCancerFeildsSection">
                            <div className="row">
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">

                                        <div className="symptomsDetaisl">
                                            <label className="checkboxLabel">
                                                <input type="checkbox" name="refusedScreening" checked={isRefused} disabled />
                                                <span>Patient has refused screening</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/polyp.png"} alt="Polyp" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>
                                                    Polyp?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="polyp" className="form-check-input" checked={cervicalData?.examination?.polyp === true} disabled />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="polyp" className="form-check-input" checked={cervicalData?.examination?.polyp === false} disabled />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/ectopy.png"} alt="Ectopy" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>
                                                    Ectopy?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="ectopy" className="form-check-input" checked={cervicalData?.examination?.ectopy === true} disabled />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="ectopy" className="form-check-input" checked={cervicalData?.examination?.ectopy === false} disabled />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/ectopy.png"} alt="Ectopy" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>
                                                    Hypertrophy?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="hypertrophy" className="form-check-input" checked={cervicalData?.examination?.hypertrophy === true} disabled />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="hypertrophy" className="form-check-input" checked={cervicalData?.examination?.hypertrophy === false} disabled />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/bleeds_on_touch.png"} alt="Bleeds on touch" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>
                                                    Bleeds on touch?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="bleedsOnTouch" className="form-check-input" checked={cervicalData?.examination?.bleedsOnTouch === true} disabled />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="bleedsOnTouch" className="form-check-input" checked={cervicalData?.examination?.bleedsOnTouch === false} disabled />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/polyp.png"} alt="Polyp" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Unhealthy cervix?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="unhealthyCervix" className="form-check-input" checked={cervicalData?.examination?.unhealthyCervix === true} disabled />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="unhealthyCervix" className="form-check-input" checked={cervicalData?.examination?.unhealthyCervix === false} disabled />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/suspecious_looking.png"} alt="Suspicious looking" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Suspicious looking?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="suspiciousLooking" className="form-check-input" checked={cervicalData?.examination?.suspiciousLooking === true} disabled />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="suspiciousLooking" className="form-check-input" checked={cervicalData?.examination?.suspiciousLooking === false} disabled />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/polyp.png"} alt="Polyp" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>
                                                    Frank malignancy/growth in cervix?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="frankMalignancy" className="form-check-input" checked={cervicalData?.examination?.frankMalignancy === true} disabled />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="frankMalignancy" className="form-check-input" checked={cervicalData?.examination?.frankMalignancy === false} disabled />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/prolapse_uterus.png"} alt="Prolapse uterus" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Prolapse uterus?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="prolapseUterus" className="form-check-input" checked={cervicalData?.examination?.prolapseUterus === true} disabled />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="prolapseUterus" className="form-check-input" checked={cervicalData?.examination?.prolapseUterus === false} disabled />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/polyp.png"} alt="Polyp" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>
                                                    Excessive discharge?</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="excessiveDischarge" className="form-check-input" checked={cervicalData?.examination?.excessiveDischarge === true} disabled />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="excessiveDischarge" className="form-check-input" checked={cervicalData?.examination?.excessiveDischarge === false} disabled />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-12">
                                    <div className="SymptomsFeild remarksRow">
                                        <div className="symptomsImg">
                                            <img src={"/images/Remarks.png"} alt="Remarks" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Remarks</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <textarea type="text" rows="3" value={cervicalData?.examination?.remarks} disabled></textarea>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="cirvicalImgSection cirvicalImgSectionCHO">

                            <div className="headerSectionBreastCHO">
                                <div className="feildsArea">
                                    <select
                                        value={mapViaValue(cervicalData?.via?.via)} disabled>
                                        <option value="">Select VIA Points</option>
                                        <option value="Positive">Positive</option>
                                        <option value="Negative">Negative</option>
                                        <option value="Suspicious">Suspicious</option>
                                    </select>
                                </div>
                                <div className="breastheadings">
                                    <h5>Select findings from dropdown and mark the points below</h5>
                                </div>
                            </div>

                            <div className="circle-container" style={{ opacity: 0.5 }}>
                                <img src={"/images/cervical_cancer_diagram.png"} style={{ width: '325px', height: '329px' }} alt="Cervical cancer diagram" />
                                {Array.from({ length: 12 }).map((_, i) => {

                                    const rotatedIndex = (i + 1) % 12; // Rotate clockwise by 1
                                    const angle = (360 / 12) * rotatedIndex - 90;
                                    const radius = 120;
                                    const x = radius * Math.cos((angle * Math.PI) / 180);
                                    const y = radius * Math.sin((angle * Math.PI) / 180);

                                    return (
                                        <div
                                            key={i}
                                            className={`dotCirvical ${selectedCervicalDots1[i] ? 'active' : ''}`}
                                            style={{
                                                transform: `translate(${x}px, ${y}px)`
                                            }}
                                        >
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="headerTitle ">
                            <div className="titleSection"><h4>Referral</h4></div>
                        </div>
                        <div className="choCancerFeildsSection">
                            <div className="row">
                                <div className="col-xs-12 col-md-4">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/Refer.png"} alt="Refer" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Refer</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <label>
                                                    <input type="radio" name="referred" className="form-check-input" checked={cervicalData?.referral?.referred === true} disabled />
                                                    <span>Yes</span>
                                                </label>
                                                <label>
                                                    <input type="radio" name="referred" className="form-check-input" checked={cervicalData?.referral?.referred === false} disabled />
                                                    <span>No</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-6">
                                    <div className="SymptomsFeild">
                                        <div className="symptomsImg">
                                            <img src={"/images/Refer.png"} alt="Refer" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Refer to {referCervical === "Yes" && <span className="text-danger">*</span>} </p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <select
                                                    value={cervicalData?.referral?.referredTo}
                                                    disabled
                                                >
                                                    <option value="">Select</option>
                                                    {/* <option value="PHC">PHC</option> */}
                                                    <option value="CHC">CHC</option>
                                                    <option value="District hospital">District Hospital</option>
                                                    <option value="Others">Other</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>If other, specify</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <input
                                                    type="text"
                                                    value={cervicalData?.referral?.referredToIfOther}
                                                    disabled
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-md-12">
                                    <div className="SymptomsFeild remarksRow">
                                        <div className="symptomsImg">
                                            <img src={"/images/Remarks.png"} alt="Remarks" />
                                        </div>
                                        <div className="symptomsDetaisl">
                                            <div className="symbtomsLabel">
                                                <p>Remarks</p>
                                            </div>
                                            <div className="symtomeFeildsForm">
                                                <textarea type="text" rows="3" value={cervicalData?.referral?.remarks} disabled></textarea>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
            <div className="separator"></div>
        </>
    );
};
